import React, { useState, useEffect } from 'react';
import '../styles/EnterAddress.css';
import { Header, Footer, PredictiveSearch, SearchInput, Loading, Button } from '../components';
import { checkOnAuthStateChanged, cloudFunctionV2, getDocument, recordEvent } from '../functions';
import { useNavigate } from 'react-router-dom';

function EnterAddress() {

    const [address, setAddress] = useState("");
    const [finalResults, setFinalResults] = useState([]);
    const [activeSearching, setActiveSearching] = useState(false);
    const [loggedIn, setLoggedIn] = useState(null);
    const [loading, setLoading] = useState(true);
    // const [preferences, setPreferences] = useState(null);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [credits, setCredits] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Enter Address | Clozers";

        const queryDocument = async(colRef, docRef) => {
            return new Promise(async (resolve, reject) => {
                const queryData = await getDocument(colRef, docRef);
                resolve(queryData);
            });
        };

        const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);

                const queryUserData = await queryDocument("users", user.userId);
                if ( queryUserData.status === 200 ) {
                    if ( queryUserData.data.data.companyId !== "" ) {
                        setCompanyId(queryUserData.data.data.companyId);
                        const queryCompanyData = await queryDocument("companies", queryUserData.data.data.companyId);
                        if ( queryCompanyData.status === 200 ) {
                            setCredits(queryCompanyData.data.data.credits);
                        }
                    }
                }
            }
            else {
                setLoggedIn(false);
            }
            setLoading(false);
        };

        fetchUserData();
    }, []);

    const changeAddress = async(val) => {
        setAddress(val);
        const url = process.env.REACT_APP_REAPI_AUTOCOMPLETE_URL;
        const autocompleteParams = {
            address: val
        };
        const getAutocomplete = await cloudFunctionV2(url, autocompleteParams);
        if ( getAutocomplete.status === 200 ) {
            const responses = getAutocomplete.data;
            if ( responses.length !== 0 ) {
                const updatedArray = responses.map((item) => {
                    return {
                        label: item.address,
                        id: item.id,
                        countyId: item.fips
                    }
                });
                setFinalResults(updatedArray);
                setActiveSearching(false);
            }
            else {
                setFinalResults([]);
                setActiveSearching(false);
            }
        }
        else {
            setFinalResults([]);
            setActiveSearching(false);
        }
    };

    const setupCompany = () => {
        recordEvent("setup_company", {});
        navigate("/company-setup");
    };

    const pickAddress = async(item) => {
        if ( loggedIn !== true ) {
            recordEvent("redirect_to_login_from_search", {
                label: item.label
            });
            navigate("/login");
            return;
        }
        else if ( credits < 1 ) {
            setFinalResults([]);
            setAddress("");
            recordEvent("no_credits", {});
            setError("You have no credits left to use the platform - contact us for more details");
            return;
        }
    
        recordEvent("property_searched", {
            label: item.label
        });
        setError(null);
        setLoading(true);
        setFinalResults([]);
        setActiveSearching(false);
        setAddress(item.label);
        const url = process.env.REACT_APP_REAPI_PROPERTY_DETAILS_URL;
        const params = {
            propertyId: item.id
        };
        const queryData = await cloudFunctionV2(url, params);
        if ( queryData.status === 200 ) {
            if ( queryData.data.id === 17338578 ) {
                queryData.data.propertyType = "SFR";
            }

            if ( queryData.data.propertyType !== "SFR" ) {
                setError("We only support Single Family Residences at the moment");
            }
            else {
                if (queryData.data.propertyInfo.bathrooms) {
                    const bathrooms = queryData.data.propertyInfo.bathrooms;
                    const decimalPart = Number((bathrooms % 1).toFixed(2));
                    let updatedBathrooms = bathrooms;
                    if (decimalPart === 0.67) {
                        updatedBathrooms = Math.floor(bathrooms) + 0.5;
                    }
                    queryData.data.propertyInfo.bathrooms = updatedBathrooms;
                }

                navigate("/verify-property", {
                    state: {
                        data: queryData.data,
                        companyId: companyId
                    }
                });
            }
        }
        setLoading(false);
    };

    return (
        <div className="enter-address-outer-container">
            <Header
                loggedIn={loggedIn}
            />
            {
                companyId === null && loggedIn === true && loading === false ?
                <div className="enter-address-inner-container relative-container">
                    <div className="enter-address-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            You need a company account to get started
                        </h1>
                        <div className="enter-address-button-container">
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={"Get started"}
                                cta={setupCompany}
                                disabled={false}
                                leftIcon={null}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={false}
                            />
                        </div>
                    </div>
                </div>
                :
                <div className="enter-address-inner-container relative-container">
                    {
                        loading === true &&
                        <Loading />
                    }
                    <div className="enter-address-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            Enter an address to get started
                        </h1>
                        <span className="body-regular colour-primary block-text text-align-center">
                            Search for the address to begin underwriting this deal.
                        </span>
                    </div>
                    <div className="enter-address-input-container relative-container">
                        <SearchInput
                            value={address}
                            onChange={(text) => changeAddress(text.target.value)}
                            placeholder="Search for any address in the U.S."
                            type="text"
                            clearInput={() => changeAddress("")}
                            magnifyingGlassFunc={null}
                            disabled={loggedIn === false ? false : (loading === true) ? true : false}
                        />
                        {
                            address === "" || (finalResults.length === 0 && activeSearching === false) ?
                            null
                            :
                            <PredictiveSearch
                                loading={activeSearching}
                                results={finalResults}
                                selectItem={(item) => pickAddress(item)}
                                nested={false}
                            />
                        }
                        {
                            error !== null &&
                            <span className="body-regular colour-error">
                                {error}
                            </span>
                        }
                    </div>
                </div>
            }
            <Footer />
        </div>
    )
};

export default EnterAddress;